import { forwardRef } from "react";
import type { LevelStatus } from "@/domain/learning/level-status";
import type { LevelSummary } from "@/domain/learning/model/level-summary";
import { Icon } from "@/presentation/components/icons/Icon";
import type { MonName } from "@/presentation/components/icons/icon-names";
import { KeyCap } from "@/presentation/components/KeyCap";
import {
  LABELS,
  LEVEL_KANJI_NAMES,
  LEVEL_RANK_NAMES,
  parseLevelNumber,
} from "@/presentation/i18n/labels";

interface LevelRowProps {
  readonly level: LevelSummary;
  readonly status: LevelStatus;
  readonly tabIndex: number;
  readonly onHoverPreload: (levelId: string) => void;
  readonly onSelect: (levelId: string) => void;
  readonly onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** 上から順に立ち上がる入場の順番（0始まり）。動きを抑える設定では無視される。 */
  readonly enterIndex?: number;
}

function statusText(status: LevelStatus): string {
  if (status.kind === "stamped") {
    return LABELS.stamp;
  }
  if (status.kind === "in-progress") {
    return `つづき ${status.clearedCount} / ${status.drillCount}`;
  }
  return "未稽古";
}

/** 進捗の割合（0〜1）。朱印済みは常に満ちた状態として扱う。 */
function progressRatio(status: LevelStatus): number {
  if (status.kind === "stamped") {
    return 1;
  }
  if (status.drillCount <= 0) {
    return 0;
  }
  return Math.min(1, status.clearedCount / status.drillCount);
}

/**
 * 段の行。**合格済みの行も明度を落とさず、錠の表現を置かない**（F-001）。
 * 進捗に関わらずすべての行が押せる（先へ進んでいなくても任意の段へ入れる）。
 *
 * D-117: 「白いカードの列」をやめ、墨の器に段位の札を嵌めた切り欠き角の面にした。
 * 番号（01〜07）は等幅で添える識別子で、段位の呼称（一段〜七段）を置き換えるものではない
 * （呼称の対応表・ui-design.md §2 は変えていない）。
 */
export const LevelRow = forwardRef<HTMLButtonElement, LevelRowProps>(function LevelRow(
  { level, status, tabIndex, onHoverPreload, onSelect, onKeyDown, enterIndex = 0 },
  ref,
) {
  const number = parseLevelNumber(level.id);
  const rank = number !== null ? (LEVEL_RANK_NAMES[number] ?? level.id) : level.id;
  const kanji = number !== null ? (LEVEL_KANJI_NAMES[number] ?? "") : "";
  const index = number !== null ? String(number).padStart(2, "0") : "--";
  const ratio = progressRatio(status);

  return (
    <button
      ref={ref}
      type="button"
      className={`level-select__row level-select__row--${status.kind}`}
      tabIndex={tabIndex}
      onMouseEnter={() => onHoverPreload(level.id)}
      onFocus={() => onHoverPreload(level.id)}
      onClick={() => onSelect(level.id)}
      onKeyDown={onKeyDown}
      data-level-id={level.id}
      style={{ "--row-enter-index": enterIndex } as React.CSSProperties}
    >
      <span className="level-select__row-plate">
        {/* 段の通し番号。読み上げでは段位（一段）が続けて読まれるため、装飾として隠す。 */}
        <span className="level-select__row-index" aria-hidden="true">
          {index}
        </span>
        <span className="level-select__row-rank">{rank}</span>
      </span>
      <span className="level-select__row-title">
        <span className="level-select__row-name">{kanji}</span>
        <div className="level-select__row-commands">
          {level.commands.map((command, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: コマンド配列は表示専用の固定リストで並び替えが起きない
            <KeyCap key={`${command}-${index}`}>{command}</KeyCap>
          ))}
        </div>
      </span>
      <span className="level-select__row-tail">
        {status.kind === "stamped" && number !== null && (
          // 朱印済みの証は、文字（「朱印」）とは別に家紋そのものでも示す（色だけに頼らない）。
          <Icon
            name={`mon-${number}` as MonName}
            size={34}
            decorative
            className="level-select__row-mon"
          />
        )}
        <span
          className={
            status.kind === "stamped"
              ? "level-select__row-status level-select__row-status--stamped"
              : "level-select__row-status"
          }
        >
          {statusText(status)}
        </span>
        <Icon name="next" size={20} decorative className="level-select__row-chevron" />
      </span>
      {/*
        行の底に敷く進捗の目盛り。数値は既に右のテキストが読み上げ対象として伝えているため、
        この目盛りは装飾として読み上げから隠す（同じ情報を二重に読ませない）。
      */}
      <span className="level-select__row-gauge" aria-hidden="true">
        <span className="level-select__row-gauge-fill" style={{ width: `${ratio * 100}%` }} />
      </span>
    </button>
  );
});
